import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Card, Chip, CardContent, Typography, Divider, CircularProgress, Alert, Box, Paper, Table, TableHead, TableBody, TableRow, TableCell, MenuItem, Select
} from "@mui/material";
import { fetchWithAuth } from "utils/fetchWithAuth";


function FicheSCI({ sciId, onChangeSCI }) {
  const [sci, setSCI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scis, setSCIs] = useState([]);
  const navigate = useNavigate();

  const statutToLabel = statut =>
  statut === "IS" ? "Impôt sur les sociétés"
    : statut === "IR" ? "Impôts sur le revenu"
      : statut || "Non défini";

  const renderPourcentage = x => {
    const value = typeof x === "number" ? x : parseFloat(x);
    if (isNaN(value)) return "-";
    return value % 1 === 0
        ? `${value} %`
        : `${value.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;
  };

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL;
    fetchWithAuth(`${API_URL}/api/scis/`)
      .then(res => res.json())
      .then(data => setSCIs(Array.isArray(data.results) ? data.results : data))
      .catch(() => setSCIs([]));
  }, []);

  useEffect(() => {
    if (!sciId) return;
    setLoading(true);
    setError(null);
    const API_URL = process.env.REACT_APP_API_URL;
    fetchWithAuth(`${API_URL}/api/scis/${sciId}/`)
      .then(res => res.json())
      .then(data => {
        setSCI(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Erreur de chargement");
        setLoading(false);
      });
  }, [sciId]);

  if (loading) return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <CircularProgress />
    </Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!sci) return <Alert severity="warning">Aucune SCI sélectionnée</Alert>;

  return (
    <Card sx={{ maxWidth: 1000, mx: "auto", mt: 4, mb: 4 }}>
      <CardContent>
        {/* Sélecteur SCI */}
        <Box sx={{ mb: 4, mt: 1, pl: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>SCI</Typography>
          <Select
            value={sci.id}
            onChange={e => navigate(`/scis/${e.target.value}`)}
            size="small"
          >
            {(Array.isArray(scis) ? scis : []).map(s =>
              <MenuItem key={s.id} value={s.id}>
                {s.raison_sociale || `SCI n°${s.id}`}
              </MenuItem>
            )}
          </Select>
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* Infos SCI */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, background: "#f7fafb" }}>
            <Typography fontWeight="bold" fontSize={17} sx={{ mb: 1 }}>
            {sci.raison_sociale}
            </Typography>
            {/* Statut d'imposition, mis en avant avec un Chip */}
            <Box sx={{ mb: 1 }}>
            <Chip
                label={statutToLabel(sci.statut_imposition)}
                color={sci.statut_imposition === "IR" ? "primary" : "secondary"}
                size="small"
                sx={{ fontWeight: 700, fontSize: 13, mr: 1 }}
            />
            </Box>
            <Typography fontSize={14}>{sci.adresse}, {sci.code_postal} {sci.ville}</Typography>
            <Typography fontSize={14}>{sci.email}</Typography>
            <Typography fontSize={14}>{sci.telephone}</Typography>
        </Paper>

        {/* Propriétaires */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Propriétaires</Typography>
        <Box sx={{ py: 1, px: 2, border: "1px solid #eee", borderRadius: 2, mb: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ mt: 1, minWidth: 600 }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#f7fafb" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Prénom</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Téléphone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Parts</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type de parts</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.isArray(sci.proprietaires) && sci.proprietaires.length > 0 ? (
                        sci.proprietaires.map((p, i) => (
                        <TableRow key={i}>
                            <TableCell>{p.proprietaire?.nom ?? "-"}</TableCell>
                            <TableCell>{p.proprietaire?.prenom ?? "-"}</TableCell>
                            <TableCell>{p.proprietaire?.email ?? "-"}</TableCell>
                            <TableCell>{p.proprietaire?.telephone ?? "-"}</TableCell>
                            <TableCell>{renderPourcentage(p.pourcentage)}</TableCell>
                            <TableCell>{p.type_droit_display}</TableCell>
                        </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Alert severity="info">Aucun propriétaire enregistré</Alert>
                            </TableCell>
                         </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
      </CardContent>
    </Card>
  );
}

export default FicheSCI;